// Invoice Approval Workflow — shared helpers (mirrors backend state machine).
//
// Simplified 2-gate flow (Vertical Head approval only):
//   proforma_pending  [VH Approval #1]  -> finance_entry
//   finance_entry      (Finance enters Invoice & Payment) -> payment_pending
//   payment_pending   [VH Approval #2]  -> completed (Paid)
//
// Vertical Head = the manager assigned to the spend's vertical, OR a Leader/Admin.
// A user can never approve an invoice they created.

export const WF_STAGES = ["proforma_pending", "finance_entry", "payment_pending", "completed"];

// Which gate is triggered by which stage (both are Vertical Head approvals).
export const GATE_FOR_STAGE = {
  proforma_pending: "G1",
  payment_pending: "G2",
};

export const PAYMENT_REQUEST_TYPES = ["Advance", "Partial", "Half", "Complete"];

// Ordered steps rendered in the stepper rail. Each maps to one backend stage.
export const WF_STEPS = [
  { stage: "proforma_pending", title: "Proforma", note: "Approval 1 · Vertical Head", gate: "G1" },
  { stage: "finance_entry", title: "Invoice & Payment", note: "Finance enters invoice & payment" },
  { stage: "payment_pending", title: "Payment", note: "Approval 2 · Vertical Head", gate: "G2" },
  { stage: "completed", title: "Completed", note: "Invoice paid & closed" },
];

// tone -> badge classes (uses existing semantic tokens only).
const TONE_CLS = {
  success: "bg-[hsl(var(--success)/0.14)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.30)]",
  info: "bg-[hsl(var(--info)/0.14)] text-[hsl(var(--info))] border-[hsl(var(--info)/0.30)]",
  warning: "bg-[hsl(var(--warning)/0.16)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.32)]",
  danger: "bg-[hsl(var(--danger)/0.14)] text-[hsl(var(--danger))] border-[hsl(var(--danger)/0.30)]",
  primary: "bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))] border-[hsl(var(--primary)/0.30)]",
  muted: "bg-[hsl(var(--surface-3))] text-muted-foreground border-[hsl(var(--border))]",
};

// Per-stage label + tone (label mirrors backend WF_STAGE_LABELS).
export const STAGE_META = {
  proforma_pending: { label: "Proforma — Awaiting Vertical Head Approval", short: "Proforma", tone: "info" },
  finance_entry: { label: "Finance — Invoice & Payment Entry", short: "Finance Entry", tone: "warning" },
  payment_pending: { label: "Payment — Awaiting Vertical Head Approval", short: "Payment Approval", tone: "info" },
  completed: { label: "Completed", short: "Completed", tone: "success" },
};

export function stageMeta(stage) {
  return STAGE_META[stage] || { label: stage || "—", short: stage || "—", tone: "muted" };
}

export function toneClass(tone) {
  return TONE_CLS[tone] || TONE_CLS.muted;
}

export function stageIndex(stage) {
  const i = WF_STAGES.indexOf(stage);
  return i === -1 ? 0 : i;
}

export function isGateStage(stage) {
  return Boolean(GATE_FOR_STAGE[stage]);
}

// True if the most recent history event was a rejection (rolled back).
export function wasRejected(spend) {
  const hist = spend?.wf_history || [];
  const last = hist[hist.length - 1];
  return Boolean(last && last.action === "rejected");
}

// ---- Role / eligibility (mirrors backend) ----------------------------------
function isFullRole(user) {
  return user?.role === "admin" || user?.role === "leader";
}

// Can this user give Vertical Head approval on this spend?
export function canApprove(user, spend) {
  if (!user) return false;
  if (user.email && user.email === spend?.created_by_email) return false;
  if (isFullRole(user)) return true;
  return user.role === "manager" && (user.assigned_verticals || []).includes(spend?.vertical);
}

export function canReject(user, spend) {
  if (!user) return false;
  if (isFullRole(user)) return true;
  return user.role === "manager" && (user.assigned_verticals || []).includes(spend?.vertical);
}

// Only Finance Executives (plus Leaders/Admins for oversight) may enter
// invoice & payment details.
export function canFinanceEntry(user) {
  return Boolean(user) && ["executive", "admin", "leader"].includes(user.role);
}

// Is this spend currently awaiting an approval that THIS user could give?
export function awaitingMyApproval(user, spend) {
  const gate = GATE_FOR_STAGE[spend?.wf_stage];
  if (!gate) return false;
  return canApprove(user, spend);
}

// Compute per-step status for the stepper given the spend's current stage.
// Returns: "complete" | "current" | "pending" | "rejected"
export function stepStatus(step, spend) {
  const cur = stageIndex(spend?.wf_stage);
  const idx = stageIndex(step.stage);
  if (spend?.wf_stage === "completed") return idx <= cur ? "complete" : "pending";
  if (idx < cur) return "complete";
  if (idx === cur) return wasRejected(spend) ? "rejected" : "current";
  return "pending";
}
